import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  FormField,
  FormItem,
  FormDescription,
  FormMessage,
  FormControl,
} from '@/shared/components/ui/form';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { ChevronDown, ChevronUp, Paperclip, Plus, Trash2, Info } from 'lucide-react';
import { BaseFieldProps } from './types';

type AttachmentEntry = {
  enabled?: string;
  filename: string;
  content: string;
  format: string;
};

interface AttachmentsFieldProps extends BaseFieldProps {}

export const AttachmentsField: React.FC<AttachmentsFieldProps> = ({
  field,
  name,
  control,
  formValues,
}) => {
  const { setValue } = useFormContext();

  const [attachments, setAttachments] = useState<AttachmentEntry[]>(() => {
    const saved = formValues?.attachments;
    if (Array.isArray(saved) && saved.length > 0) return saved as AttachmentEntry[];
    return [];
  });
  const [isOpen, setIsOpen] = useState(attachments.length > 0);

  const updateAttachments = (next: AttachmentEntry[]) => {
    setAttachments(next);
    setValue('attachments', next);
  };

  const addAttachment = () => {
    updateAttachments([...attachments, { filename: '', content: '', format: 'pdf' }]);
    setIsOpen(true);
  };

  const removeAttachment = (index: number) => {
    updateAttachments(attachments.filter((_, i) => i !== index));
  };

  const updateField = (index: number, key: keyof AttachmentEntry, value: string) => {
    const next = [...attachments];
    next[index] = { ...next[index], [key]: value };
    updateAttachments(next);
  };

  return (
    <FormField
      control={control}
      name={name}
      render={() => (
        <FormItem>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 text-xs px-2"
              onClick={() => setIsOpen((v) => !v)}
            >
              <Paperclip className="h-3.5 w-3.5" />
              {field.label ?? 'Anexos'} ({attachments.length})
              {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </Button>
            <span title={'Anexe arquivos ao email usando placeholders de Node Output Memory.\nEx: ${"Clean Transcription".output.content}'}>
              <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
            </span>
          </div>

          <FormControl>
            {isOpen ? (
              <div className="space-y-3 mt-2">
                {attachments.map((att, idx) => (
                  <div key={idx} className="rounded-md border bg-muted/40 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">Anexo {idx + 1}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                        onClick={() => removeAttachment(idx)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Habilitado (placeholder ou true/false)</Label>
                      <Input
                        className="h-7 text-xs"
                        value={att.enabled ?? ''}
                        onChange={(e) => updateField(idx, 'enabled', e.target.value)}
                        placeholder='${parameters.send_transcript} ou true/false'
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Nome do arquivo (sem extensão)</Label>
                      <Input
                        className="h-7 text-xs"
                        value={att.filename}
                        onChange={(e) => updateField(idx, 'filename', e.target.value)}
                        placeholder='Transcription_${"Summarize Meeting".output.meeting.title}'
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Conteúdo (placeholder)</Label>
                      <Input
                        className="h-7 text-xs"
                        value={att.content}
                        onChange={(e) => updateField(idx, 'content', e.target.value)}
                        placeholder='${"Clean Transcription".output.content}'
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Formato (pdf, md, txt ou placeholder)</Label>
                      <Input
                        className="h-7 text-xs"
                        value={att.format}
                        onChange={(e) => updateField(idx, 'format', e.target.value)}
                        placeholder='pdf, md, txt ou ${parameters.transcript_format}'
                      />
                    </div>
                  </div>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1 text-xs"
                  onClick={addAttachment}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Adicionar anexo
                </Button>
              </div>
            ) : (
              <div />
            )}
          </FormControl>

          {field.description && (
            <FormDescription>{field.description}</FormDescription>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default AttachmentsField;
